import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import './TeacherAssignmentsView.css';
import './TeacherDetailPages.css';

function TeacherAssignmentsView({ teacherData }) {
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('upcoming');
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailGrades, setDetailGrades] = useState([]);
  const [detailStudentNames, setDetailStudentNames] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    assignmentType: 'homework',
    classId: '',
    subjectId: '',
    subjectName: '',
    maxScore: '',
    dueDate: ''
  });
  const [availableSubjects, setAvailableSubjects] = useState([]);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) return;
    console.log('🔥 TeacherAssignments: Loading classes for teacher', teacherData.uid);
    console.log('🔥 TeacherAssignments: Teacher data', teacherData);
    try {
      // First, load ALL classes to see what's in the database
      console.log('🔥 TeacherAssignments: Loading ALL classes for debugging');
      const allClassesQuery = query(collection(db, 'classes'));
      const allClassesSnapshot = await getDocs(allClassesQuery);
      const allClassesData = allClassesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherAssignments: ALL classes in database', allClassesData.length, allClassesData);
      console.log('🔥 TeacherAssignments: Class teacherIds', allClassesData.map(c => ({ id: c.id, name: c.name, teacherId: c.teacherId })));

      // Load classes for this teacher
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherAssignments: Classes loaded for teacher', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 TeacherAssignments: Error loading classes:', error);
    }
  }, [teacherData]);

  const loadAssignments = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    console.log('🔥 TeacherAssignments: Loading assignments for teacher', teacherData.uid);
    console.log('🔥 TeacherAssignments: Classes available', classes.length, classes);
    try {
      // Load assignments from each class's assignments subcollection (like iOS)
      const assignmentsData = [];
      for (const cls of classes) {
        console.log('🔥 TeacherAssignments: Loading assignments for class', cls.id, cls.name);
        try {
          const assignmentsQuery = query(
            collection(db, 'classes', cls.id, 'assignments')
          );
          const snapshot = await getDocs(assignmentsQuery);
          console.log('🔥 TeacherAssignments: Assignments snapshot for class', cls.id, 'count:', snapshot.docs.length);
          const classAssignments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          assignmentsData.push(...classAssignments);
        } catch (error) {
          console.error('🔥 TeacherAssignments: Error loading assignments for class', cls.id, error);
        }
      }

      console.log('🔥 TeacherAssignments: Total assignments loaded', assignmentsData.length, assignmentsData);
      setAssignments(assignmentsData);
    } catch (error) {
      console.error('🔥 TeacherAssignments: Error loading assignments:', error);
    } finally {
      setLoading(false);
    }
  }, [classes, teacherData]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadAssignments();
    }
  }, [classes, loadAssignments]);

  const loadSubjectsForClass = async (classId) => {
    if (!classId) {
      setAvailableSubjects([]);
      return;
    }
    try {
      const subjectsSnapshot = await getDocs(
        collection(db, 'classes', classId, 'subjects')
      );
      const subjectsData = subjectsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(s => s.isActive !== false);
      setAvailableSubjects(subjectsData);
    } catch (error) {
      console.error('Error loading subjects for class', classId, error);
      setAvailableSubjects([]);
    }
  };

  const getFilteredAssignments = () => {
    const now = new Date();
    switch (selectedFilter) {
      case 'upcoming':
        return assignments.filter(a => {
          const dueDate = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
          return dueDate >= now && a.isActive !== false;
        }).sort((a, b) => {
          const dateA = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
          const dateB = b.dueDate?.toDate ? b.dueDate.toDate() : new Date(b.dueDate);
          return dateA - dateB;
        });
      case 'past':
        return assignments.filter(a => {
          const dueDate = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
          return dueDate < now;
        }).sort((a, b) => {
          const dateA = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
          const dateB = b.dueDate?.toDate ? b.dueDate.toDate() : new Date(b.dueDate);
          return dateB - dateA;
        });
      default:
        return assignments;
    }
  };

  const resetForm = () => {
    setNewAssignment({
      title: '',
      description: '',
      assignmentType: 'homework',
      classId: '',
      subjectId: '',
      subjectName: '',
      maxScore: '',
      dueDate: ''
    });
    setAvailableSubjects([]);
  };

  const handleAddAssignment = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'classes', newAssignment.classId, 'assignments'), {
        ...newAssignment,
        maxScore: parseFloat(newAssignment.maxScore),
        dueDate: new Date(newAssignment.dueDate),
        createdBy: teacherData?.uid,
        createdAt: new Date(),
        isActive: true,
        schoolId: teacherData?.schoolId || null
      });
      setShowAddForm(false);
      resetForm();
      loadAssignments();
    } catch (error) {
      console.error('Error adding assignment:', error);
      alert('Failed to add assignment');
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    if (!confirm('Are you sure you want to delete this assignment?')) return;
    try {
      await deleteDoc(doc(db, 'classes', assignment.classId, 'assignments', assignment.id));
      loadAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Failed to delete assignment');
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  // Open the full-screen assignment detail page and load this assignment's grades
  // with student names resolved (iOS TeacherAssignmentDetailView parity)
  const openAssignmentDetail = async (assignment) => {
    console.log('🔥 TeacherAssignments: Assignment tapped', assignment.title, assignment.id);
    setSelectedAssignment(assignment);
    setShowDetail(true);
    setDetailLoading(true);
    setDetailGrades([]);
    setDetailStudentNames({});
    try {
      const studentsSnap = await getDocs(collection(db, 'classes', assignment.classId, 'students'));
      const names = {};
      studentsSnap.docs.forEach(d => {
        const s = d.data();
        if (s.fullName) {
          names[d.id] = s.fullName;
          if (s.studentId) names[s.studentId] = s.fullName;
        }
      });
      console.log('🔥 TeacherAssignments: Student names resolved', Object.keys(names).length);
      setDetailStudentNames(names);

      const gradesSnap = await getDocs(
        collection(db, 'classes', assignment.classId, 'assignments', assignment.id, 'grades')
      );
      const gradesData = gradesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log('🔥 TeacherAssignments: Grades loaded for detail', gradesData.length, gradesData);
      setDetailGrades(gradesData);
    } catch (error) {
      console.error('🔥 TeacherAssignments: Error loading assignment detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const getStudentName = (studentId) => detailStudentNames[studentId] || studentId || 'Unknown';

  const getAssignmentIcon = (type) => {
    const icons = {
      homework: '📝',
      quiz: '❓',
      test: '📋',
      project: '🎯',
      exam: '📚',
      classwork: '✏️'
    };
    return icons[type] || '📝';
  };

  const filteredAssignments = getFilteredAssignments();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Full-screen assignment detail page (replaces the old cramped modal)
  if (showDetail && selectedAssignment) {
    const detailClass = classes.find(c => c.id === selectedAssignment.classId);
    return (
      <div className="detail-page">
        <button className="detail-back-button" onClick={() => setShowDetail(false)}>
          ◄ Back to Assignments
        </button>
        <div className="detail-hero">
          <div className="detail-avatar">{getAssignmentIcon(selectedAssignment.assignmentType)}</div>
          <div className="detail-hero-info">
            <h2>{selectedAssignment.title}</h2>
            <p className="detail-subtitle">{detailClass?.name || 'Unknown Class'}{detailClass?.grade ? ` · ${detailClass.grade}` : ''}</p>
            <div className="detail-badges">
              <span className="detail-badge">{selectedAssignment.assignmentType || 'Assignment'}</span>
              {selectedAssignment.subjectName && <span className="detail-badge gray">{selectedAssignment.subjectName}</span>}
              <span className={`detail-badge ${selectedAssignment.isActive === false ? 'red' : 'green'}`}>
                {selectedAssignment.isActive === false ? 'Inactive' : 'Active'}
              </span>
              {selectedAssignment.isInClassOnly === true && <span className="detail-badge orange">In-Class Only</span>}
            </div>
          </div>
          <div className="detail-hero-actions">
            <button
              className="detail-btn detail-btn-delete"
              onClick={() => {
                handleDeleteAssignment(selectedAssignment);
                setShowDetail(false);
              }}
            >
              🗑️ Delete Assignment
            </button>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-card">
            <h3>Assignment Details</h3>
            <div className="detail-field"><span className="field-label">Max Score</span><span className="field-value">{selectedAssignment.maxScore ?? 'N/A'} pts</span></div>
            <div className="detail-field"><span className="field-label">Due Date</span><span className="field-value">{formatDate(selectedAssignment.dueDate)}</span></div>
            <div className="detail-field"><span className="field-label">Class</span><span className="field-value">{detailClass?.name || 'Unknown'}</span></div>
            <div className="detail-field"><span className="field-label">Subject</span><span className="field-value">{selectedAssignment.subjectName || 'None'}</span></div>
            <div className="detail-field"><span className="field-label">Status</span><span className="field-value">{selectedAssignment.isActive === false ? 'Inactive' : 'Active'}</span></div>
            <div className="detail-field"><span className="field-label">Created</span><span className="field-value">{formatDate(selectedAssignment.createdAt)}</span></div>
          </div>

          <div className="detail-card">
            <h3>Description</h3>
            {selectedAssignment.description
              ? <p className="detail-empty" style={{ color: '#374151' }}>{selectedAssignment.description}</p>
              : <p className="detail-empty">No description provided</p>}
          </div>

          <div className="detail-card full-width">
            <h3>Student Grades ({detailGrades.length})</h3>
            {detailLoading ? (
              <p className="detail-empty">Loading grades…</p>
            ) : detailGrades.length === 0 ? (
              <p className="detail-empty">No grades recorded for this assignment yet</p>
            ) : (
              <div className="detail-list">
                {detailGrades.map(g => {
                  const pct = g.maxScore ? Math.round((g.score / g.maxScore) * 100) : null;
                  return (
                    <div key={g.id} className="detail-list-item">
                      <span className="item-name">{getStudentName(g.studentId)}</span>
                      <span className="item-meta">{g.score}/{g.maxScore}{pct !== null ? ` (${pct}%)` : ''}{g.gradedAt ? ` · ${formatDate(g.gradedAt)}` : ''}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-assignments-view">
      <h2>Assignments</h2>

      <div className="filter-bar">
        <button
          className={`filter-button ${selectedFilter === 'past' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('past')}
        >
          Past
        </button>
        <button
          className={`filter-button ${selectedFilter === 'upcoming' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('upcoming')}
        >
          Upcoming
        </button>
        <button
          className={`filter-button ${selectedFilter === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('all')}
        >
          All
        </button>
        <button
          className="add-button"
          onClick={() => setShowAddForm(true)}
        >
          + Add Assignment
        </button>
      </div>

      {showAddForm && (
        <div className="add-assignment-form">
          <h3>Create New Assignment</h3>
          <form onSubmit={handleAddAssignment}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={newAssignment.title}
                onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newAssignment.description}
                onChange={(e) => setNewAssignment({ ...newAssignment, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <select
                  value={newAssignment.assignmentType}
                  onChange={(e) => setNewAssignment({ ...newAssignment, assignmentType: e.target.value })}
                >
                  <option value="homework">Homework</option>
                  <option value="quiz">Quiz</option>
                  <option value="test">Test</option>
                  <option value="project">Project</option>
                  <option value="exam">Exam</option>
                  <option value="classwork">Classwork</option>
                </select>
              </div>
              <div className="form-group">
                <label>Class *</label>
                <select
                  value={newAssignment.classId}
                  onChange={(e) => {
                    const classId = e.target.value;
                    setNewAssignment({ ...newAssignment, classId, subjectId: '', subjectName: '' });
                    loadSubjectsForClass(classId);
                  }}
                  required
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} ({cls.grade})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <select
                  value={newAssignment.subjectId}
                  onChange={(e) => {
                    const subjectId = e.target.value;
                    const subject = availableSubjects.find(s => s.id === subjectId);
                    setNewAssignment({
                      ...newAssignment,
                      subjectId,
                      subjectName: subject ? subject.name : ''
                    });
                  }}
                  required
                  disabled={availableSubjects.length === 0}
                >
                  <option value="">
                    {availableSubjects.length === 0 ? 'Select a class first' : 'Select Subject'}
                  </option>
                  {availableSubjects.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Max Score *</label>
                <input
                  type="number"
                  step="0.1"
                  value={newAssignment.maxScore}
                  onChange={(e) => setNewAssignment({ ...newAssignment, maxScore: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Due Date *</label>
                <input
                  type="date"
                  value={newAssignment.dueDate}
                  onChange={(e) => setNewAssignment({ ...newAssignment, dueDate: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => { setShowAddForm(false); resetForm(); }}>
                Cancel
              </button>
              <button type="submit">Create Assignment</button>
            </div>
          </form>
        </div>
      )}

      {filteredAssignments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No {selectedFilter} assignments</h3>
          <p>Create your first assignment to get started</p>
        </div>
      ) : (
        <div className="assignments-list">
          {filteredAssignments.map(assignment => {
            const classInfo = classes.find(c => c.id === assignment.classId);
            return (
              <div
                key={assignment.id}
                className="assignment-card"
                onClick={() => openAssignmentDetail(assignment)}
              >
                <div className="assignment-icon">{getAssignmentIcon(assignment.assignmentType)}</div>
                <div className="assignment-info">
                  <h4>{assignment.title}</h4>
                  <p className="assignment-type">{assignment.assignmentType} {assignment.subjectName ? `• ${assignment.subjectName}` : ''}</p>
                  <p className="assignment-class">{classInfo?.name || 'Unknown Class'}</p>
                  <p className="assignment-due">Due: {formatDate(assignment.dueDate)}</p>
                  {assignment.description && (
                    <p className="assignment-description">{assignment.description}</p>
                  )}
                </div>
                <div className="assignment-score">
                  <span className="score-value">{assignment.maxScore}</span>
                  <span className="score-label">pts</span>
                </div>
                <button
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAssignment(assignment);
                  }}
                >
                  🗑️
                </button>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

export default TeacherAssignmentsView;
